import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner, Textarea } from '@zt/ui';
import { ConfirmButton } from './Confirm';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { ComplianceRow, Country } from '../lib/types';

/**
 * Ürünün ülke bazlı uygunluğu (F7-12, ADR-117): ülkede satışa açık/kapalı (gerekçeyle), kayıt/izin numarası ve teyit
 * durumu. Kayıt yoksa ürün her ülkede satışa açıktır. Kapalı ürün o ülkenin pazarında vitrinde listelenmez ve
 * satın alınamaz.
 */
const REASONS = [
  'registration_missing',
  'legal_restriction',
  'shipping_restriction',
  'business_decision',
  'other',
] as const;
const VERIFICATION = ['unverified', 'verified', 'not_required'] as const;
const verificationTone = { verified: 'moss', unverified: 'amber', not_required: 'slate' } as const;

export function ProductComplianceSection({ productId }: { productId: string }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const canWrite = can('catalog.product.write');
  const q = useQuery({
    queryKey: ['product-compliance', productId],
    queryFn: () => api.get<ComplianceRow[]>(`/v1/admin/products/${productId}/compliance`),
  });
  const [edit, setEdit] = useState<ComplianceRow | 'new' | null>(null);
  const del = useApiMutation(
    (country: string) => api.del(`/v1/admin/products/${productId}/compliance/${country}`),
    { invalidate: [['product-compliance', productId]], success: t('common.deleted') },
  );
  return (
    <Section
      title={t('admin.compliance.title')}
      description={t('admin.compliance.lead')}
      action={
        <span className="inline-flex items-center gap-1">
          {canWrite && (
            <Button size="sm" onClick={() => setEdit('new')}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.compliance.add')}
            </Button>
          )}
          <HelpTip k="products.compliance" />
        </span>
      }
    >
      {q.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : q.data?.length ? (
        <ul
          className="divide-y divide-line rounded-md border border-line"
          aria-label={t('admin.compliance.title')}
        >
          {q.data.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
              <div className="min-w-[14rem] flex-1">
                <p className="font-medium">
                  {r.countryName ?? r.countryCode} <span className="text-ink-3">({r.countryCode})</span>
                </p>
                <p className="text-sm text-ink-2">
                  {r.saleStatus === 'blocked' && r.blockReason
                    ? t(`admin.compliance.reasons.${r.blockReason}`)
                    : t('admin.compliance.allowedLead')}
                  {r.registrationNo
                    ? ` · ${t('admin.compliance.registrationShort', { no: r.registrationNo })}`
                    : ''}
                </p>
                {r.verifiedAt && (
                  <p className="text-sm text-ink-3">
                    {t('admin.compliance.verifiedAt', { date: formatDate(r.verifiedAt, true) })}
                  </p>
                )}
                {r.note && <p className="text-sm text-ink-3">{r.note}</p>}
              </div>
              <Badge tone={r.saleStatus === 'blocked' ? 'rose' : 'moss'}>
                {t(`admin.compliance.status.${r.saleStatus}`)}
              </Badge>
              <Badge tone={verificationTone[r.verificationStatus]}>
                {t(`admin.compliance.verification.${r.verificationStatus}`)}
              </Badge>
              {canWrite && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setEdit(r)}>
                    {t('common.edit')}
                  </Button>
                  <ConfirmButton
                    message={t('admin.compliance.confirmDelete', { country: r.countryName ?? r.countryCode })}
                    onConfirm={() => del.mutateAsync(r.countryCode)}
                  >
                    {t('common.delete')}
                  </ConfirmButton>
                </>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title={t('admin.compliance.empty')} description={t('admin.compliance.emptyLead')} />
      )}
      {edit && (
        <ComplianceDialog
          productId={productId}
          row={edit === 'new' ? null : edit}
          taken={(q.data ?? []).map((r) => r.countryCode)}
          onClose={() => setEdit(null)}
        />
      )}
    </Section>
  );
}

function ComplianceDialog({
  productId,
  row,
  taken,
  onClose,
}: {
  productId: string;
  row: ComplianceRow | null;
  taken: string[];
  onClose: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const countries = useQuery({
    queryKey: ['countries'],
    queryFn: () => api.get<Country[]>('/v1/admin/countries'),
    staleTime: 3_600_000,
  });
  const options = (countries.data ?? []).filter(
    (c) => row?.countryCode === c.code || !taken.includes(c.code),
  );
  const [f, setF] = useState({
    countryCode: row?.countryCode ?? '',
    saleStatus: row?.saleStatus ?? ('blocked' as ComplianceRow['saleStatus']),
    blockReason: row?.blockReason ?? '',
    note: row?.note ?? '',
    registrationNo: row?.registrationNo ?? '',
    verificationStatus: row?.verificationStatus ?? ('unverified' as ComplianceRow['verificationStatus']),
  });
  const country = f.countryCode || options[0]?.code || '';
  const save = useApiMutation(
    () =>
      api.put(
        `/v1/admin/products/${productId}/compliance`,
        {
          countryCode: country,
          saleStatus: f.saleStatus,
          blockReason: f.saleStatus === 'blocked' ? f.blockReason || null : null,
          note: f.note.trim() || null,
          registrationNo: f.registrationNo.trim() || null,
          verificationStatus: f.verificationStatus,
        },
        row ? { ifMatch: row.version } : undefined,
      ),
    {
      invalidate: [['product-compliance', productId]],
      success: t('common.saved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={row ? t('admin.compliance.editTitle') : t('admin.compliance.add')}
      description={t('admin.compliance.dialogLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!country}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.compliance.country')} error={fieldError(save.error, 'countryCode')}>
          {(p) => (
            <Select
              {...p}
              value={country}
              disabled={!!row}
              onChange={(e) => setF({ ...f, countryCode: e.target.value })}
            >
              {options.map((c) => (
                <option key={c.code} value={c.code}>
                  {locale === 'en' ? c.nameEn : c.nameTr} ({c.code})
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.compliance.saleStatus')} help={<HelpTip k="products.compliance_status" />}>
          {(p) => (
            <Select
              {...p}
              value={f.saleStatus}
              onChange={(e) => setF({ ...f, saleStatus: e.target.value as ComplianceRow['saleStatus'] })}
            >
              <option value="blocked">{t('admin.compliance.status.blocked')}</option>
              <option value="allowed">{t('admin.compliance.status.allowed')}</option>
            </Select>
          )}
        </Field>
        {f.saleStatus === 'blocked' && (
          <Field
            label={t('admin.compliance.reason')}
            required
            help={<HelpTip k="products.compliance_reason" />}
            error={fieldError(save.error, 'blockReason')}
          >
            {(p) => (
              <Select
                {...p}
                value={f.blockReason}
                onChange={(e) => setF({ ...f, blockReason: e.target.value })}
              >
                <option value="">—</option>
                {REASONS.map((r) => (
                  <option key={r} value={r}>
                    {t(`admin.compliance.reasons.${r}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field
          label={t('admin.compliance.registrationNo')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="products.compliance_registration" />}
          description={t('admin.compliance.registrationHint')}
          error={fieldError(save.error, 'registrationNo')}
        >
          {(p) => (
            <Input
              {...p}
              value={f.registrationNo}
              onChange={(e) => setF({ ...f, registrationNo: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.compliance.verificationLabel')}
          help={<HelpTip k="products.compliance_verification" />}
        >
          {(p) => (
            <Select
              {...p}
              value={f.verificationStatus}
              onChange={(e) =>
                setF({ ...f, verificationStatus: e.target.value as ComplianceRow['verificationStatus'] })
              }
            >
              {VERIFICATION.map((v) => (
                <option key={v} value={v}>
                  {t(`admin.compliance.verification.${v}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.compliance.note')} optionalLabel={t('common.optional')}>
          {(p) => (
            <Textarea {...p} rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
